// Import package components
import { useContext, useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import classnames from "classnames";
import { Flex, Image } from "uikit-react";
import { useCookies } from "react-cookie";

import TeamAdd from "./add";
import TeamDelete from "./delete";
import TableLoading from "../common/Loading/table_loading";
import { Link } from "../../ui";
import { AppContexts } from "../../providers";
import Pagination from "../common/Pagination";
import RecordsPerPage from "../common/RecordsPerPage";

const Team = () => {
  const [criteria, setCriteria] = useState({
    page: 0,
    search_term: "",
    sort: "updated_on",
  });
  const { set, settings, loading, active } = useContext(
    AppContexts.SettingsContext,
  );
  const inboxCtx = useContext(AppContexts.InboxContext);

  const [dirty, setDirty] = useState(false);
  const [rec, setRec] = useState(null);
  const [inbox, setInbox] = useState(null);

  const [edits, setEdits] = useState({});
  const [deletes, setDeletes] = useState({});
  const [cookies] = useCookies(["records-per-page"]);
  const { "records-per-page": recordsPerPage = 20 } = cookies;

  useEffect(() => {
    document.title = "User Groups - RueBaRue";
    if (!!inboxCtx.settings) {
      setInbox(inboxCtx.settings);
    }
  }, [inboxCtx.settings, inboxCtx.loading]);

  useEffect(() => {
    inboxCtx.get();
  }, []);

  const recipients = useMemo(() => {
    if (!inbox) {
      return [];
    } else {
      if (!inbox.recipients) {
        return [];
      } else {
        return inbox.recipients
          .filter((recipient) => {
            if (
              [recipient.first_name, recipient.last_name]
                .join(" ")
                .toLowerCase()
                .search(criteria.search_term.toLowerCase()) >= 0 &&
              !recipient.deleted
            )
              return true;
            return false;
          })
          .sort((prev, next) => {
            if (criteria.sort === "updated_on")
              return next.updated_at?.localeCompare(prev.updated_at);
            if (criteria.sort === "first_name")
              return prev.first_name?.localeCompare(next.first_name);
            if (criteria.sort === "last_name")
              return prev.last_name?.localeCompare(next.last_name);
            return true;
          });
      }
    }
  }, [inbox, criteria]);

  const pagedRecipients = useMemo(
    () =>
      recipients.slice(
        criteria.page * recordsPerPage,
        (criteria.page + 1) * recordsPerPage,
      ),
    [recipients, criteria, recordsPerPage],
  );


  const handleIndexChanged = useCallback((newPageIndex) => {
    handleCriteriaChange("page", newPageIndex);
  });

  const handleAdd = () => {
    inboxCtx.initRecipient();
    inboxCtx.setIsNew(true);
  };

  const handleEdit = (r) => {
    inboxCtx.setRecipient(JSON.parse(JSON.stringify(r)));
    inboxCtx.setIsNew(false);
  };

  const handleAlertToggle = (e, r) => {
    inboxCtx.updateRecipient({
      ...r,
      alerts: e.target.checked,
    });
  };

  const handleDelete = (r) => {
    inboxCtx.setDeletedRecipient(r);
  };

  const handleInvite = (r) => {
    setRec(r);
    inboxCtx.inviteRecipient(r);
  };

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((criteria) => ({ ...criteria, [key]: value }));
  });

  const getTeamSchedule = (dow) => {
    let min = null;
    let max = null;

    (inbox?.recipients || []).map((r) => {
      if (r.deleted) {
        return;
      }

      (r.schedule || []).map((wd) => {
        if (wd?.weekday === dow) {
          let st = moment(wd.start_time.split(/[\s|T]/)[1], "HH:mm:ss");
          let et = moment(wd.end_time.split(/[\s|T]/)[1], "HH:mm:ss");

          if (!min || st.isBefore(min)) {
            min = st;
          }

          if (!max || et.isAfter(max)) {
            max = et;
          }
        }
      });
    });

    if (!!min && !!max) {
      return `${min.format("h:mma")} - ${max.format("h:mma")}`;
    } else {
      return "Off";
    }
  };

  function groupAndSummarize() {
    const schedule = [
      { day: "Sun", time: getTeamSchedule(1) },
      { day: "Mon", time: getTeamSchedule(2) },
      { day: "Tue", time: getTeamSchedule(3) },
      { day: "Wed", time: getTeamSchedule(4) },
      { day: "Thu", time: getTeamSchedule(5) },
      { day: "Fri", time: getTeamSchedule(6) },
      { day: "Sat", time: getTeamSchedule(7) },
    ];
    // Reverse time ranges
    const reversed = schedule.map((day) => {
      if (day.time === "Off") return { ...day, reversedTime: "all day" };
  
      const [start, end] = day.time.split(" - ");
      return { ...day, reversedTime: `${end} to ${start}` };
    });
  
    // Group days by time ranges
    const grouped = {};
    reversed.forEach(({ day, reversedTime }) => {
      if (!grouped[reversedTime]) grouped[reversedTime] = [];
      grouped[reversedTime].push(day);
    });
  
    // Generate summarized sentence
    const summarized = Object.entries(grouped).map(([timeRange, days]) => {
      if (timeRange === "all day") return `all day on ${days.join(", ")}`;
  
      // Single day vs multiple days
      if (days.length === 1) {
        return `${timeRange} on ${days[0]}`;
      } else {
        return `${timeRange} from ${days[0]} to ${days[days.length - 1]}`;
      }
    });
  
    return summarized.join(" and ");
  }  

  return (
    <>
      <section id="team-schedule-template">
        <div id="hms-page-title">
          <h1 className="uk-heading-small">User Groups</h1>
        </div>
        <div id="hms-main-body">
          <div className="uk-flex uk-flex-between uk-flex-top">
            <div className="hms-guest-btn">
              <button
                className="uk-button hms-btn hms-red-btn"
                uk-toggle="target: #team-add"
                onClick={handleAdd}
              >
                <span uk-icon="icon: plus; ratio: 0.7"></span> User Group
              </button>
            </div>
          </div>
          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
            <div className="uk-overflow-auto">
              <Flex alignment="between top" className="guest-form-top-filters">
                <div>
                  <form
                    action=""
                    className="uk-form uk-flex uk-flex-middle hms-form"
                  >
                    <div className="uk-inline">
                      <div className="uk-search uk-search-default">
                        {criteria.search_term && criteria.search_term !== "" ? (
                          <a
                            className="uk-form-icon-flip uk-form-icon"
                            uk-icon="icon: close"
                            onClick={() =>
                              handleCriteriaChange("search_term", "")
                            }
                          ></a>
                        ) : (
                          <span
                            className="uk-search-icon-flip"
                            uk-search-icon="true"
                          />
                        )}
                        <input
                          className="uk-search-input"
                          type="search"
                          placeholder="Name"
                          value={criteria.search_term}
                          onChange={(event) =>
                            handleCriteriaChange(
                              "search_term",
                              event.target.value,
                            )
                          }
                        />
                      </div>
                    </div>
                  </form>
                </div>
                <div>
                  <div className="hms-sortby-btn">
                    <button className="uk-button" type="button">
                      SORT BY<span uk-icon="icon: triangle-down"></span>
                    </button>
                    <div uk-dropdown="mode: click; offset: 0; animation: uk-animation-slide-top-small; duration: 400">
                      <ul className="uk-nav uk-dropdown-nav">
                        <li
                          className={classnames({
                            "uk-active": criteria.sort === "updated_on",
                          })}
                        >
                          <Link
                            href="#"
                            onClick={() =>
                              handleCriteriaChange("sort", "updated_on")
                            }
                          >
                            Last Updated
                          </Link>
                        </li>
                        <li
                          className={classnames({
                            "uk-active": criteria.sort === "first_name",
                          })}
                        >
                          <Link
                            href="#"
                            onClick={() =>
                              handleCriteriaChange("sort", "first_name")
                            }
                          >
                            Group name
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Flex>
              <div>
                Group properties and contacts to restrict user access to specific properties, contacts and messages.
              </div>
              <table className="uk-table uk-table-hover uk-table-divider hms-table hms-table-team">
                <thead>
                  <tr>
                    <th className="uk-table-expand name-field">Group Name</th>
                    <th className="uk-table-expand name-field">Description</th>
                    <th className="uk-table-expand">Number of Users</th>
                    <th className="uk-table-expand">Number of Home Guides</th>
                    <th className="uk-table-expand">Number of Contacts</th>
                    <th className="uk-width-small">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <div className="semi-bold-font">
                        Default
                      </div>
                    </td>
                    <td>
                      All users are automatically added to this group, granting them access to all messages.
                    </td>
                    <td>3</td>
                    <td>2</td>
                    <td>4</td>
                    <td className="hms-action-btn">
                      <div className="uk-flex uk-flex-top uk-flex-between">
                        <div className="uk-flex">
                          <div>
                            <button
                              className="uk-button"
                              uk-toggle="target: #team-add"
                              uk-tooltip="Edit"
                            >
                              <Image src="/images/edit.svg" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div className="semi-bold-font">
                        Webchat Leads
                      </div>
                    </td>
                    <td>
                      This group is for users to read and respond to messages from Webchat leads.
                    </td>
                    <td>3</td>
                    <td>-</td>
                    <td>-</td>
                    <td className="hms-action-btn">
                      <div className="uk-flex uk-flex-top uk-flex-between">
                        <div className="uk-flex">
                          <div>
                            <button
                              className="uk-button"
                              uk-toggle="target: #team-add"
                              uk-tooltip="Edit"
                            >
                              <Image src="/images/edit.svg" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div className="semi-bold-font">
                        Florida
                      </div>
                    </td>
                    <td>
                      To read, respond to messages, and view guest information for properties in Florida. 
                    </td>
                    <td>3</td>
                    <td>2</td>
                    <td>4</td>
                    <td className="hms-action-btn">
                      <div className="uk-flex uk-flex-top uk-flex-between">
                        <div className="uk-flex">
                          <div>
                            <button
                              className="uk-button"
                              uk-toggle="target: #team-add"
                              uk-tooltip="Edit"
                            >
                              <Image src="/images/edit.svg" />
                            </button>
                          </div>
                          <div>
                            <button
                              className="uk-button"
                              uk-toggle="target: #team-delete"
                              uk-tooltip="Delete"
                            >
                              <Image src="/images/delete.svg" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div className="semi-bold-font">
                        Florida - Housekeepers
                      </div>
                    </td>
                    <td>
                      To read, respond to messages, and manage contact information for housekeepers in Florida. 
                    </td>
                    <td>3</td>
                    <td>2</td>
                    <td>4</td>
                    <td className="hms-action-btn">
                      <div className="uk-flex uk-flex-top uk-flex-between">
                        <div className="uk-flex">
                          <div>
                            <button
                              className="uk-button"
                              uk-toggle="target: #team-add"
                              uk-tooltip="Edit"
                            >
                              <Image src="/images/edit.svg" />
                            </button>
                          </div>
                          <div>
                            <button
                              className="uk-button"
                              uk-toggle="target: #team-delete"
                              uk-tooltip="Delete"
                            >
                              <Image src="/images/delete.svg" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div className="semi-bold-font">
                        Florida - Owners
                      </div>
                    </td>
                    <td>
                      To read, respond to messages, and manage contact information for owners in Florida.
                    </td>
                    <td>3</td>
                    <td>2</td>
                    <td>4</td>
                    <td className="hms-action-btn">
                      <div className="uk-flex uk-flex-top uk-flex-between">
                        <div className="uk-flex">
                          <div>
                            <button
                              className="uk-button"
                              uk-toggle="target: #team-add"
                              uk-tooltip="Edit"
                            >
                              <Image src="/images/edit.svg" />
                            </button>
                          </div>
                          <div>
                            <button
                              className="uk-button"
                              uk-toggle="target: #team-delete"
                              uk-tooltip="Delete"
                            >
                              <Image src="/images/delete.svg" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div className="semi-bold-font">
                        Florida - Operations
                      </div>
                    </td>
                    <td>
                      Florida Operations Group
                    </td>
                    <td>2</td>
                    <td>0</td>
                    <td>0</td>
                    <td className="hms-action-btn">
                      <div className="uk-flex uk-flex-top uk-flex-between">
                        <div className="uk-flex">
                          <div>
                            <button
                              className="uk-button"
                              uk-toggle="target: #team-add"
                              uk-tooltip="Edit"
                            >
                              <Image src="/images/edit.svg" />
                            </button>
                          </div>
                          <div>
                            <button
                              className="uk-button"
                              uk-toggle="target: #team-delete"
                              uk-tooltip="Delete"
                            >
                              <Image src="/images/delete.svg" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div className="semi-bold-font">
                        Georgia
                      </div>
                    </td>
                    <td>
                      To read, respond to messages, and view guest information for properties in Georgia.
                    </td>
                    <td>3</td>
                    <td>2</td>
                    <td>4</td>
                    <td className="hms-action-btn">
                      <div className="uk-flex uk-flex-top uk-flex-between">
                        <div className="uk-flex">
                          <div>
                            <button
                              className="uk-button"
                              uk-toggle="target: #team-add"
                              uk-tooltip="Edit"
                            >
                              <Image src="/images/edit.svg" />
                            </button>
                          </div>
                          <div>
                            <button
                              className="uk-button"
                              uk-toggle="target: #team-delete"
                              uk-tooltip="Delete"
                            >
                              <Image src="/images/delete.svg" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div className="semi-bold-font">
                        Georgia - Housekeepers
                      </div>
                    </td>
                    <td>
                      To read, respond to messages, and manage contact information for housekeepers in Georgia.
                    </td>
                    <td>3</td>
                    <td>-</td>
                    <td>4</td>
                    <td className="hms-action-btn">
                      <div className="uk-flex uk-flex-top uk-flex-between">
                        <div className="uk-flex">
                          <div>
                            <button
                              className="uk-button"
                              uk-toggle="target: #team-add"
                              uk-tooltip="Edit"
                            >
                              <Image src="/images/edit.svg" />
                            </button>
                          </div>
                          <div>
                            <button
                              className="uk-button"
                              uk-toggle="target: #team-delete"
                              uk-tooltip="Delete"
                            >
                              <Image src="/images/delete.svg" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div className="semi-bold-font">
                        Georgia - Owners
                      </div>
                    </td>
                    <td>
                      To read, respond to messages, and manage contact information for owners in Georgia.
                    </td>
                    <td>2</td>
                    <td>-</td>
                    <td>4</td>
                    <td className="hms-action-btn">
                      <div className="uk-flex uk-flex-top uk-flex-between">
                        <div className="uk-flex">
                          <div>
                            <button
                              className="uk-button"
                              uk-toggle="target: #team-add"
                              uk-tooltip="Edit"
                            >
                              <Image src="/images/edit.svg" />
                            </button>
                          </div>
                          <div>
                            <button
                              className="uk-button"
                              uk-toggle="target: #team-delete"
                              uk-tooltip="Delete"
                            >
                              <Image src="/images/delete.svg" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div className="semi-bold-font">
                        Florida - Bookkeeping
                      </div>
                    </td>
                    <td>
                      Georgia Bookkeeping Group
                    </td>
                    <td>2</td>
                    <td>0</td>
                    <td>0</td>
                    <td className="hms-action-btn">
                      <div className="uk-flex uk-flex-top uk-flex-between">
                        <div className="uk-flex">
                          <div>
                            <button
                              className="uk-button"
                              uk-toggle="target: #team-add"
                              uk-tooltip="Edit"
                            >
                              <Image src="/images/edit.svg" />
                            </button>
                          </div>
                          <div>
                            <button
                              className="uk-button"
                              uk-toggle="target: #team-delete"
                              uk-tooltip="Delete"
                            >
                              <Image src="/images/delete.svg" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <Flex alignment="between top" className="guest-form-top-filters">
            <Pagination
              count={recipients.length}
              index={criteria.page}
              onIndexChanged={handleIndexChanged}
            />
            {recipients.length > 0 && <RecordsPerPage />}
          </Flex>

        </div>
      </section>
      <TeamAdd />
      <TeamDelete />
    </>
  );
};

export default Team;
